import { createFileRoute } from "@tanstack/react-router";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { LegalPage, legalHead, type LegalSection } from "@/components/public/legal-page";
import {
  CONTACT_EMAIL,
  SELLER_COUNTRY_AR,
  SELLER_COUNTRY_EN,
  SELLER_LEGAL_NAME_AR,
  SELLER_LEGAL_NAME_EN,
} from "@/lib/legal/seller";

export const Route = createFileRoute("/_public/refund")({
  head: () =>
    legalHead(
      "/refund",
      "سياسة الاسترداد والإلغاء والتسليم",
      "متى يحق لك استرداد المبلغ، كيف تلغي أو تغيّر موعد الاستشارة، ومتى يصلك تقرير البوصلة الذكية.",
    ),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: RefundPage,
});

const ar: LegalSection[] = [
  {
    title: "القاعدة: كل المبيعات نهائية",
    body: [
      "لا يُسترَدّ المبلغ عند الإلغاء من طرف العميل، أو عدم حضور الجلسة، أو تغيير الرأي.",
      "القاعدة تشمل الجلسات المفردة، وباقة الـ 4 جلسات، والبوصلة الذكية، والدورات المدفوعة.",
    ],
  },
  {
    title: "استثناءات من طرفنا (وليست استرداداً عند طلب العميل)",
    body: [
      "تغيير الموعد مجاني حتى 24 ساعة قبل الموعد المؤكَّد.",
      "إذا ألغينا نحن الجلسة أو ما قدرنا نعقدها (ومنها أي مشكلة تقنية من طرفنا): لك أن تختار موعداً جديداً أو استرداد مبلغ تلك الجلسة.",
      "الدفع المكرَّر أو الخاطئ يُرَدّ دائماً.",
      "البوصلة الذكية: استرداد كامل إذا لم يصل التقرير خلال 72 ساعة من إتمام نموذج التفضيلات. وإذا كان التقرير لا يطابق الإجابات المقدَّمة، نصحّحه مجاناً.",
    ],
  },
  {
    title: "تسليم البوصلة الذكية",
    body: "يصل التقرير إلى حسابك على المنصّة خلال 24 ساعة من إتمام نموذج التفضيلات.",
  },
  {
    title: "طريقة الطلب في حالات الاستثناء",
    body: [
      `المراسلة على ${CONTACT_EMAIL} أو على واتساب مع تفاصيل الطلب (الاسم، الخدمة، تاريخ الدفع وطريقته).`,
      "نردّ خلال 48 ساعة عمل.",
      "في حالات الاستثناء، نردّ المبلغ بنفس طريقة الدفع (PayPal، شام كاش أو حوالة الهرم) خلال 10 أيام عمل من الموافقة.",
      "نرجو التواصل معنا أولاً قبل فتح أي نزاع على الدفع — غالباً منحلّها بسرعة.",
    ],
  },
];

const en: LegalSection[] = [
  {
    title: "The rule: all sales are final",
    body: [
      "There is no refund when a client cancels, misses a session, or changes their mind.",
      "This applies to single sessions, the 4-session package, Smart Bousala and paid courses.",
    ],
  },
  {
    title: "Provider-side exceptions (not client refunds)",
    body: [
      "Free rescheduling up to 24 hours before the confirmed session.",
      "If we cancel or cannot hold a session (including a technical problem on our side): you choose a new appointment or a refund of that session.",
      "Duplicate or erroneous payments are always returned.",
      "Smart Bousala: full refund if the report is not delivered within 72 hours of completing the preferences form. If it does not match the answers you gave, we correct it free of charge.",
    ],
  },
  {
    title: "Smart Bousala delivery",
    body: "The report is delivered to your account within 24 hours of completing the preferences form.",
  },
  {
    title: "How to make a request in an exception case",
    body: [
      `Email ${CONTACT_EMAIL} or message us on WhatsApp with your order details (name, service, payment date and method).`,
      "We reply within 48 business hours.",
      "In exception cases, we return the amount to the same payment method (PayPal, Sham Cash or Al-Haram transfer) within 10 business days of approval.",
      "Please contact us before opening a payment dispute.",
    ],
  },
];

function RefundPage() {
  return (
    <LegalPage
      kicker="الاسترداد والإلغاء"
      title="سياسة الاسترداد والإلغاء والتسليم"
      updatedAr="تشرين الأول 2026"
      intro={
        <p>
          تُدار هذه المنصّة ومنتجاتها المدفوعة من قبل {SELLER_LEGAL_NAME_AR}، مؤسسة فردية مقرّها{" "}
          {SELLER_COUNTRY_AR}. للتواصل: {CONTACT_EMAIL}
        </p>
      }
      sections={ar}
      enTitle="Refund, Cancellation & Delivery Policy"
      updatedEn="October 2026"
      enIntro={
        <p>
          This website and its paid products are operated by {SELLER_LEGAL_NAME_EN}, a sole
          proprietor based in the {SELLER_COUNTRY_EN}. Contact: {CONTACT_EMAIL}
        </p>
      }
      enSections={en}
    />
  );
}
